import { Environment as GuestAccountEnv } from '@rcg/favorites';
import { Brand, BrandAlpha3Code, BrandCode, ClientSideConfig, EnvConfig, EnvironmentColor, ServerSideConfig } from 'types';
import commonConfig from './config.common';
import devEnvConfig from './config.dev';
import devTstEnvConfig from './config.dev-tst';
import prdEnvConfig from './config.prd';
import stgEnvConfig from './config.stg';
import tstEnvConfig from './config.tst';

interface BrandHost {
	brandCode: BrandCode;
	hosts: string[];
}

function getBrand(brandCode?: BrandCode): Brand {
	const brandAlpha3Codes = getBrandAlpha3Codes();

	return {
		code: brandCode,
		alpha3Code: brandAlpha3Codes[brandCode],
	};
}

export function getBrandAlpha3Codes(): Record<BrandCode, BrandAlpha3Code> {
	return {
		[BrandCode.Celebrity]: BrandAlpha3Code.Celebrity,
		[BrandCode.RoyalCaribbean]: BrandAlpha3Code.RoyalCaribbean,
	};
}

export function getBrandCodeByHost(host: string = ''): BrandCode | undefined {
	const BrandHosts: BrandHost[] = [
		{
			brandCode: BrandCode.Celebrity,
			hosts: ['celebrity.com', 'celebritycruises.com'],
		},
		{
			brandCode: BrandCode.RoyalCaribbean,
			hosts: ['royalcaribbean.com'],
		},
	];

	for (const currentBrandHost of BrandHosts) {
		if (currentBrandHost.hosts.some((h) => host.includes(`.${h}`))) {
			return currentBrandHost.brandCode;
		}
	}

	return undefined;
}

function getEnv(): string {
	return process.env.AWS_STAGE ?? 'dev';
}

function getEnvConfig(env: string): EnvConfig {
	switch (env) {
		case 'prd':
			return prdEnvConfig;

		case 'stg':
			return stgEnvConfig;

		case 'tst':
			return tstEnvConfig;

		case 'dev-tst':
			return devTstEnvConfig;

		default:
			return devEnvConfig;
	}
}

function getEnvVarValue(key: string, fallback?: string): string {
	const value = process.env[key] ?? fallback;

	if (!value) {
		throw new Error(`Environment variable not found: ${key}`);
	}

	return value;
}

function getGuestAccountEnv(brand: Brand, env: string): GuestAccountEnv {
	switch (env) {
		case 'prd':
		case 'stg':
			return GuestAccountEnv.PRODUCTION;
		case 'tst':
		case 'dev':
			if (brand.code === BrandCode.Celebrity) {
				return GuestAccountEnv.STAGE;
			}
			return GuestAccountEnv.TEST;
		default:
			throw new Error('Invalid environment');
	}
}

export function getClientSideConfig(brandCode: BrandCode, env: string, envColor: EnvironmentColor, appVersion): ClientSideConfig {
	const brand: Brand = getBrand(brandCode);
	const envConfig: EnvConfig = getEnvConfig(env);

	return {
		env,
		envColor,
		guestAccountEnv: getGuestAccountEnv(brand, env),
		brand,
		appVersion,
		baseUrl: envConfig?.brands?.[brandCode]?.baseUrl,
		imageBaseUrl: commonConfig?.brands?.[brandCode]?.imageBaseUrl,
		mapsBaseUrl: envConfig?.brands?.[brandCode]?.mapsBaseUrl,
		endpoints: {
			...commonConfig?.endpoints,
			...envConfig?.endpoints,
		},
		externalUrls: {
			...commonConfig?.externalUrls,
		},
		publicKeys: {
			...commonConfig?.publicKeys,
			...envConfig?.publicKeys,
			...envConfig?.brands?.[brandCode]?.publicKeys,
		},
		scripts: {
			...envConfig?.brands?.[brandCode]?.scripts,
		},
		resources: {
			...envConfig?.brands?.[brandCode]?.resources,
		},
	};
}

export function getServerSideConfig(brandCode: BrandCode): ServerSideConfig {
	const brand: Brand = getBrand(brandCode);
	const env = getEnv();
	const envColor: EnvironmentColor = getEnvVarValue('ENVIRONMENT_COLOR', EnvironmentColor.Blue) as EnvironmentColor;
	const appVersion = getEnvVarValue('GIT_SHORT_SHA', 'No version found');
	const envConfig: EnvConfig = getEnvConfig(env);

	return {
		env,
		envColor,
		appVersion,
		brand,
		baseUrl: envConfig?.brands?.[brandCode]?.baseUrl,
		endpoints: {
			cruiseSearchApi: commonConfig?.endpoints?.cruiseSearchApi,
			cruiseStandaloneSearchApi: commonConfig?.endpoints?.cruiseStandaloneSearchApi,
			globalizationApi: commonConfig?.endpoints?.globalizationApi,
			campaignsApi: commonConfig?.endpoints?.campaignsApi,
			cruiseSearchTimeout: commonConfig?.endpoints?.cruiseSearchTimeout,
			dictionaryApi: envConfig?.endpoints.dictionaryApi,
			universalNavigationApi: commonConfig?.brands?.[brandCode]?.endpoints?.universalNavigationApi,
		},
		dictionaries: {
			cruiseSearchClient: commonConfig?.dictionaries.cruiseSearchClient,
			booking: commonConfig?.dictionaries.booking,
			bookingErrors: commonConfig?.dictionaries.bookingErrors,
		},
		secrets: {
			launchDarklySdkKey: getEnvVarValue('LAUNCH_DARKLY_SDK_KEY'),
			launchDarklyCommonSdkKey: getEnvVarValue('LAUNCH_DARKLY_COMMON_SDK_KEY'),
		},
	};
}
